/**
 * Helper: Vue
 * -----------------------------------------------------------------------------
 * Shared Vue methods and prop validation.
 * - Add in alphabetical order.
 *
 */

/**
 * Compute.
 * -----------------------------------------------------------------------------
 * Compute properties.
 *
 */

/**
 * Compute aspect ratio.
 * @param {Boolean|Number|String} ratio - Aspect ratio.
 * @param {Object} image - Image object.
 * @returns {String}
 */
export function computeAspectRatio(ratio, image) {
  /**
   * If ratio is false or 'image' then use image object properties.
   */
  if (ratio === false || ratio === 'image') {
    if (image.aspectRatio || image.aspect_ratio) {
      return image.aspectRatio || image.aspect_ratio;
    }

    return image.width / image.height;
  }

  /**
   * If ratio is a number then return without changes.
   */
  if (typeof ratio === 'number') {
    return ratio;
  }

  /**
   * Replace colon to use CSS aspect-ratio format.
   */
  return ratio.replace(':', ' / ');
}

/**
 * Compute discount logic data.
 * @param {Object} data - Data object.
 * @param {Boolean} [data.discountPreview] - Show campaign preview.
 * @param {Boolean} [data.onSale] - Product/variant has a sale price - compare
 * at price is greater than regular price.
 * @param {Boolean|Object} data.product - Formatted product response.
 * @param {Boolean|Object} [data.variant] - Formatted variant response.
 * @returns {Object}
 */
export function computeDiscountLogic({
  discountPreview,
  onSale,
  product,
  variant,
} = {}) {
  const collectionIds = product?.collectionIds?.length
    ? product.collectionIds
    : [];

  return {
    collectionIds,
    discountPreview,
    onSale,
    productId: product?.id,
    variantId: variant?.id,
  };
}

/**
 * Compute padding bottom.
 * @param {Number|String} aspectRatio - Calculated aspect ratio.
 */
export function computePaddingBottom(aspectRatio) {
  let decimal = aspectRatio;

  if (typeof aspectRatio === 'string') {
    const parts = aspectRatio.split('/');
    decimal = Number(parts[0]) / Number(parts[1]);
  }

  return `${(1 / decimal) * 100.0}%`;
}

/**
 * Emits.
 * -----------------------------------------------------------------------------
 * Validate emits.
 *
 */

/**
 * Emit and validate update request object emit.
 * @param {*} payload - Passed emit payload.
 * @returns {Boolean}
 */
export function emitUpdateRequestObject(payload) {
  return (
    typeof payload.type === 'string' &&
    (payload.response === null ||
      ['boolean', 'object'].includes(typeof payload.response))
  );
}

/**
 * Emit and validate update variant emit.
 * - Boolean - variant has been reset.
 * - null - no variant found, don't reset selected options.
 * - Object - new variant.
 * @param {*} payload - Passed emit payload.
 * @returns {Boolean}
 */
export function emitUpdateVariant(payload) {
  return (
    payload === null ||
    (typeof payload === 'object' && payload?.id) ||
    typeof payload === 'boolean'
  );
}

/**
 * Validation.
 * -----------------------------------------------------------------------------
 * Validate props.
 *
 */

/**
 * Validate image prop.
 * @param {Boolean|String} value - Passed prop value.
 * @returns {Object}
 */
export function validateImage(value) {
  return !Object.keys(value).length || value.src || value.url;
}

/**
 * Validate ratio prop.
 * @param {Boolean|Number|String} value - Passed prop value.
 * @param {Boolean} desktop - Is desktop ratio prop?
 * @returns {Object}
 */
export function validateRatio(value, desktop) {
  switch (typeof value) {
    case 'boolean':
      return (
        (desktop && typeof value === 'boolean') || (!desktop && value === false)
      );
    case 'number':
      return true;
    case 'string':
      return (
        value === '' ||
        value === 'image' ||
        value.match(/^^(?<width>\d+\.?\d*)\s\/\s(?<height>\d+\.?\d*)$/g) ||
        value.match(/^(?<width>\d+\.?\d*):(?<height>\d+\.?\d*)$/g)
      );
    default:
      return false;
  }
}

/**
 * Server time composable for Vue components
 * -----------------------------------------------------------------------------
 * Provides reactive server time functionality for Vue components.
 */

import { onBeforeUnmount, onMounted, ref } from 'vue';
import {
  fetchServerTime,
  getCurrentServerTime,
  startServerTimeTracking,
} from './general.js';

/**
 * Vue composable for server time management.
 * @param {Object} options - Configuration options
 * @param {boolean} [options.autoStart=true] - Whether to automatically start tracking
 * @param {number} [options.interval=1000] - Update interval in milliseconds
 * @param {boolean} [options.autoFetch=true] - Whether to automatically fetch initial server time
 * @returns {Object} Reactive server time object with methods
 */
export function useServerTime(options = {}) {
  const { autoStart = true, interval = 1000, autoFetch = true } = options;

  const serverTime = ref(null);
  const currentTime = ref(null);
  const isLoading = ref(false);
  const error = ref(null);
  let stopTracking = null;

  /**
   * Fetch server time and update reactive state.
   */
  const fetchTime = async () => {
    isLoading.value = true;
    error.value = null;

    try {
      const time = await fetchServerTime();
      serverTime.value = time;
      currentTime.value = time;
      return time;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Start time tracking.
   */
  const startTracking = () => {
    if (stopTracking) {
      stopTracking();
    }

    stopTracking = startServerTimeTracking((time) => {
      currentTime.value = time;
    }, interval);
  };

  /**
   * Stop time tracking.
   */
  const stop = () => {
    if (stopTracking) {
      stopTracking();
      stopTracking = null;
    }
  };

  /**
   * Initialize server time.
   */
  const initialize = async (startTrackingAfterFetch = autoStart) => {
    if (autoFetch) {
      await fetchTime();
    }

    if (startTrackingAfterFetch) {
      startTracking();
    }
  };

  // Lifecycle hooks
  onMounted(() => {
    initialize();
  });

  onBeforeUnmount(() => {
    stop();
  });

  return {
    // Reactive state
    serverTime,
    currentTime,
    isLoading,
    error,

    // Methods
    fetchTime,
    startTracking,
    stop,
    initialize,

    // Computed-like getters
    get currentServerTime() {
      return currentTime.value || getCurrentServerTime();
    },
  };
}
